import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import './AdminGallery.css';

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    category: 'sunday-service',
    date: '',
    description: '',
    imageFiles: []
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const categories = [
    { id: 'sunday-service', name: 'Sunday Services' },
    { id: 'events', name: 'Church Events' },
    { id: 'youth', name: 'Youth Ministry' },
    { id: 'worship', name: 'Worship & Praise' },
    { id: 'community', name: 'Community Outreach' },
    { id: 'special', name: 'Special Occasions' }
  ];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get('/api/gallery');
      // Ensure data is an array
      setImages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]); // Set empty array on error
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setUploadForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (files) => {
    if (!files || files.length === 0) return;

    const validFiles = [];
    const newPreviews = [];
    const errors = [];

    // Process each file
    Array.from(files).forEach((file, index) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Not an image file`);
        return;
      }

      // Validate file size (100GB)
      if (file.size > 100 * 1024 * 1024 * 1024) {
        errors.push(`${file.name}: File must be less than 100GB`);
        return;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push({
          file: file,
          preview: reader.result,
          name: file.name
        });
        setImagePreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });

    if (errors.length > 0) {
      setUploadStatus(`⚠️ ${errors.length} file(s) skipped: ${errors.join('; ')}`);
    } else {
      setUploadStatus('');
    }

    setUploadForm(prev => ({ 
      ...prev, 
      imageFiles: [...prev.imageFiles, ...validFiles]
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadForm.imageFiles.length === 0) {
      setUploadStatus('Please select at least one image');
      return;
    }

    setLoading(true);
    setUploadStatus(`⏳ Uploading ${uploadForm.imageFiles.length} image(s)...`);
    setUploadProgress({});

    const formData = new FormData();
    formData.append('title', uploadForm.title);
    formData.append('category', uploadForm.category);
    formData.append('date', uploadForm.date);
    formData.append('description', uploadForm.description);
    
    // Append all image files
    uploadForm.imageFiles.forEach((file) => {
      formData.append('images', file);
    });

    try {
      const response = await axios.post('/api/gallery', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress({ percent: percentCompleted });
        }
      });

      const successCount = response.data.images?.length || 0;
      const errorCount = response.data.errors?.length || 0;
      
      let statusMessage = `✅ Successfully uploaded ${successCount} image(s)!`;
      if (errorCount > 0) {
        statusMessage += ` (${errorCount} failed)`;
      }
      
      setUploadStatus(statusMessage);
      setUploadForm({
        title: '',
        category: 'sunday-service',
        date: '',
        description: '',
        imageFiles: []
      });
      setImagePreviews([]);
      setUploadProgress({});
      fetchImages();
      setTimeout(() => setUploadStatus(''), 5000);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`Error: ${error.response?.data?.error || 'Upload failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (imageId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await axios.delete(`/api/gallery/${imageId}`);
      fetchImages();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting image');
    }
  };

  const handleSyncFromAzure = async () => {
    if (!window.confirm('This will recover images from Azure Blob Storage that are not in the gallery. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/gallery/sync-from-azure');
      alert(`${response.data.message}\n\nAdded: ${response.data.added} images\nTotal: ${response.data.total} images`);
      fetchImages();
    } catch (error) {
      console.error('Sync error:', error);
      alert(`Error: ${error.response?.data?.error || 'Failed to sync images'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-gallery">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2>📸 Photo Gallery Management</h2>
          <p className="section-description">
            Upload new photos or manage existing gallery images
          </p>
        </div>
        <button 
          onClick={handleSyncFromAzure} 
          className="btn btn-secondary"
          disabled={loading}
          style={{ marginLeft: '1rem' }}
          title="Recover images from Azure Blob Storage"
        >
          🔄 Sync from Azure
        </button>
      </div>

      {/* Upload Form */}
      <div className="upload-section">
        <h3>Upload New Photo</h3>
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Photo Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={uploadForm.title}
                onChange={handleFormChange}
                required
                placeholder="e.g., Sunday Morning Worship"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={uploadForm.category}
                onChange={handleFormChange}
                required
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="date">Date *</label>
              <input
                type="date"
                id="date"
                name="date"
                value={uploadForm.date}
                onChange={handleFormChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={uploadForm.description}
              onChange={handleFormChange}
              rows="3"
              placeholder="Optional description..."
            />
          </div>

          {/* Drag & Drop Zone */}
          <div
            className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {imagePreviews.length > 0 ? (
              <div className="image-previews-container">
                <div className="image-previews-grid">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={preview.preview} alt={preview.name} />
                      <button
                        type="button"
                        onClick={() => {
                          const newPreviews = imagePreviews.filter((_, i) => i !== index);
                          const newFiles = uploadForm.imageFiles.filter((_, i) => i !== index);
                          setImagePreviews(newPreviews);
                          setUploadForm(prev => ({ ...prev, imageFiles: newFiles }));
                        }}
                        className="remove-preview"
                        title="Remove"
                      >
                        ✕
                      </button>
                      <div className="preview-name">{preview.name}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e.target.files)}
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    id="fileInput"
                  />
                  <label htmlFor="fileInput" className="btn btn-secondary btn-small">
                    + Add More
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreviews([]);
                      setUploadForm(prev => ({ ...prev, imageFiles: [] }));
                    }}
                    className="btn btn-secondary btn-small"
                  >
                    Clear All
                  </button>
                  <small style={{ marginLeft: 'auto' }}>
                    {imagePreviews.length} file(s) selected
                  </small>
                </div>
              </div>
            ) : (
              <>
                <div className="drop-icon">📤</div>
                <p>Drag & drop your images here, or click to browse</p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                  You can select multiple images at once
                </p>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(e.target.files)}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  id="fileInput"
                />
                <label htmlFor="fileInput" className="btn btn-secondary">
                  Choose Files
                </label>
                <small>Max size: 100GB per file. Formats: JPG, PNG, WebP</small>
              </>
            )}
          </div>
          
          {uploadProgress.percent !== undefined && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ 
                width: '100%', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '4px', 
                overflow: 'hidden',
                height: '24px'
              }}>
                <div style={{
                  width: `${uploadProgress.percent}%`,
                  backgroundColor: '#FFD700',
                  height: '100%',
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#000',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                  {uploadProgress.percent}%
                </div>
              </div>
            </div>
          )}

          {uploadStatus && (
            <div className={`upload-status ${uploadStatus.includes('✅') ? 'success' : uploadStatus.includes('❌') ? 'error' : ''}`}>
              {uploadStatus}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-gold"
            disabled={loading || uploadForm.imageFiles.length === 0}
          >
            {loading 
              ? `⏳ Uploading ${uploadForm.imageFiles.length} image(s)...` 
              : `📤 Upload ${uploadForm.imageFiles.length > 0 ? uploadForm.imageFiles.length : ''} Photo${uploadForm.imageFiles.length !== 1 ? 's' : ''}`}
          </button>
        </form>
      </div>

      {/* Existing Images */}
      <div className="existing-images">
        <h3>Existing Photos ({images.length})</h3>
        <div className="images-grid">
          {images.map(image => (
            <div key={image.id} className="image-card">
              <div className="image-wrapper">
                <img src={image.imageUrl} alt={image.title} />
              </div>
              <div className="image-details">
                <h4>{image.title}</h4>
                <p className="image-date">{new Date(image.date).toLocaleDateString()}</p>
                <p className="image-category">{categories.find(c => c.id === image.category)?.name}</p>
                <button
                  onClick={() => handleDelete(image.id, image.imageUrl)}
                  className="btn btn-danger btn-small"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;

